import { useEffect } from 'react';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  isConfirming?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  isConfirming = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isConfirming) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isConfirming, onClose]);

  const confirmClass =
    tone === 'danger'
      ? 'bg-del hover:bg-del/90 text-white'
      : 'bg-accent hover:bg-accent/90 text-bg';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isConfirming ? onClose : undefined}
      />
      <div className="relative w-full max-w-sm overflow-hidden border border-rule bg-panel shadow-2xl">
        <div className="px-6 py-5">
          <div className="flex items-start gap-3">
            {tone === 'danger' ? (
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-del/15">
                <svg
                  className="h-5 w-5 text-del"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            ) : null}
            <div className="flex-1">
              <h3 className="text-base font-semibold text-ink">{title}</h3>
              <p className="mt-1 text-sm text-dim whitespace-pre-line">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-rule-soft px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="px-3 py-1.5 text-sm font-medium text-ink hover:bg-bg disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming}
            className={`px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${confirmClass}`}
          >
            {isConfirming ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
